import type { ApplicationAudio } from "../audio/applicationAudio"
import { type DirectionResult, localizationConfigSchema } from "../localization/direction"
import { buttonElement, htmlElement, inputElement, selectElement } from "./dom"

type AudioProvider = () => ApplicationAudio

function renderDirection(result: DirectionResult): void {
  switch (result.kind) {
    case "bearing":
      htmlElement("bearing-value").textContent = `${result.azimuthDegrees.toFixed(1)}°`
      htmlElement("bearing-arrow").style.transform = `rotate(${result.azimuthDegrees}deg)`
      htmlElement("bearing-arrow").style.opacity = String(0.25 + result.confidence * 0.75)
      htmlElement("confidence-value").textContent =
        `Confidence ${Math.round(result.confidence * 100)}%`
      htmlElement("ambiguity-value").textContent = result.mirrored
        ? "Mirrored bearing"
        : "2D azimuth"
      htmlElement("direction-status").textContent =
        "Live GCC-PHAT estimate from synchronized channels."
      return
    case "insufficient_signal":
      htmlElement("direction-status").textContent =
        `No reliable bearing: ${result.reason.replaceAll("_", " ")}.`
      return
  }
}

export function bindDirectionControls(provider: AudioProvider): () => void {
  let directionTimer: number | undefined
  const button = buttonElement("direction-toggle")

  button.addEventListener("click", () => {
    if (directionTimer !== undefined) {
      window.clearInterval(directionTimer)
      directionTimer = undefined
      button.textContent = "Start direction finder"
      return
    }

    const app = provider()
    const microphoneCount = Number(selectElement("array-geometry").value)
    const snapshot = app.snapshot()
    if (snapshot === undefined || snapshot.channelCount < microphoneCount) {
      htmlElement("direction-status").textContent =
        `Input A exposes ${snapshot?.channelCount ?? 0} channel(s); ${microphoneCount} synchronized channels are required.`
      return
    }

    button.textContent = "Stop direction finder"
    directionTimer = window.setInterval(() => {
      const parsed = localizationConfigSchema.safeParse({
        sampleRate: app.context.sampleRate,
        microphoneCount,
        spacingMetres: inputElement("mic-spacing").valueAsNumber / 100,
        temperatureCelsius: inputElement("temperature").valueAsNumber,
      })
      if (parsed.success) renderDirection(app.direction(parsed.data))
    }, 250)
  })

  inputElement("mic-spacing").addEventListener("input", () => {
    htmlElement("mic-spacing-value").textContent = `${inputElement("mic-spacing").valueAsNumber} cm`
  })
  inputElement("temperature").addEventListener("input", () => {
    htmlElement("temperature-value").textContent = `${inputElement("temperature").valueAsNumber} °C`
  })

  return () => {
    if (directionTimer !== undefined) window.clearInterval(directionTimer)
  }
}
